import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
  getActionRequiredForTasks,
  getActionRequiredFromRelationships,
} from 'support/helpers/relationships/actionRequired';
import { RelationshipActionRequired, RelationshipV2 } from 'support/types';
import { ActionRequiredBadge } from 'components/ActionRequiredBadge/ActionRequiredBadge';

import { useTasksByRelationshipId } from './hooks/useTasksByRelationshipId';

type ConnectionsActionRequiredBadgeProps = {
  relationships: Array<RelationshipV2>;
  currentPartnerId: string;
};

export const ConnectionsActionRequiredBadge = ({
  relationships,
  currentPartnerId,
}: ConnectionsActionRequiredBadgeProps) => {
  const { t } = useTranslation();

  const tasksByRelationshipId = useTasksByRelationshipId(relationships);

  const actionRequiredStatuses = getActionRequiredFromRelationships(relationships, currentPartnerId);
  const taskActionRequired = getActionRequiredForTasks(relationships, currentPartnerId, tasksByRelationshipId);
  const allActionRequired = Array.from(new Set([...actionRequiredStatuses, ...taskActionRequired]));

  const text = getActionRequiredText(allActionRequired, t);

  return text ? <ActionRequiredBadge text={text} /> : null;
};

const getActionRequiredText = (actionRequired: Array<RelationshipActionRequired>, t: TFunction): string | undefined => {
  if (!actionRequired || actionRequired.length === 0) {
    return undefined;
  }

  const needsTestDocument = actionRequired.includes('READY_FOR_TESTING');
  const needsApproval = actionRequired.includes('HAS_TEST_MESSAGES') || actionRequired.includes('IN_REVIEW');
  const hasPendingTasks = actionRequired.includes('TASKS_PENDING');

  const actionCount = [needsTestDocument, needsApproval, hasPendingTasks].filter(Boolean).length;

  if (actionCount > 1) {
    return t('connections:table.fields.actionRequired.multiple');
  }
  if (needsTestDocument) {
    return t('connections:table.fields.actionRequired.testDocumentRequired');
  }
  if (needsApproval) {
    return t('connections:table.fields.actionRequired.approvalRequired');
  }
  if (hasPendingTasks) {
    return t('connections:table.fields.actionRequired.pendingTasks');
  }

  return undefined;
};
